// src/components/organization/portal/Partners.jsx
import React from "react";
import {Divider} from "@mui/material";
import useTranslation from "../../../i18n/useTranslation";

/**
 * Partners
 *
 * Usage:
 *  <Partners
 *      theme={theme}
 *      partners={[
 *        { name: "AngeonX Inc.", logoUrl: "/dist/img/angeonx-white.png", href: "https://angeonx.com" },
 *        { name: "AngeonX Sports", logoUrl: "/dist/img/angeonx-sports.png", href: "https://kbolasport.com" },
 *        { name: "Local Academy", logoUrl: "/dist/img/partners/academy.png" },
 *      ]}
 *  />
 */

const PARTNERS = [
    {
        name: "AngeonX Inc.",
        logoUrl: "/dist/img/angeonx-logo-transparent.png",
        href: "https://angeonx.com",
    },
    {
        name: "AngeonX Sports",
        logoUrl: "/dist/img/angeonx-sports.png",
        href: "https://kbolasports.com",
    },
];

export default function Partners({ partners = PARTNERS, theme }) {
    const {t} = useTranslation();

    if (!partners.length) return null;

    const {
        surfaceColor,
        borderColor,
        textOnBackground,
        textOnSurface,
        accentColor,
        primaryColor
    } = theme || {};

    const stripBg = surfaceColor || "#FFFFFF";
    const stripBorder = borderColor || "rgba(0,0,0,0.06)";
    const headingTitleColor = textOnSurface || textOnBackground || "#111827";
    const headingSubtitleColor = `${headingTitleColor}CC`;
    const dividerColor = accentColor || headingTitleColor;
    const highlight = accentColor || "#D5FF5F";

    const css = `
      .partners-section {
        margin: 0 auto 40px;
        max-width: 1200px;
        padding: 0 20px;
      }

      .partners-section__heading {
        margin-bottom: 12px;
      }

      .partners-section__title {
        margin: 0;
        font-size: 18px;
        font-weight: 800;
        letter-spacing: 0.06em;
        color: ${primaryColor};
      }

      .partners-section__subtitle {
        margin: 4px 0 6px;
        font-size: 13px;
        font-weight: 500;
        color: ${headingSubtitleColor};
      }

      .partners-section__divider {
        width: 80px;
        height: 2px;
        border-radius: 999px;
        background-color: ${dividerColor};
      }

      .partners-strip {
        margin-top: 12px;
        max-width: 100%;
        padding: 12px 20px;
        border-radius: 16px;
        border: 1px solid ${stripBorder};
        background: radial-gradient(circle at top left, ${highlight}18, transparent 55%),
                    ${stripBg};
        box-shadow: 0 10px 30px rgba(0,0,0,0.12);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 14px;
        text-align: center;
      }

      .partners-strip__logos {
        flex: 1 1 auto;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 18px;
        flex-wrap: wrap;
      }

      .partners-strip__item {
        flex: 0 0 auto;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 6px 10px;
        border-radius: 999px;
        background: rgba(255,255,255,0.9);
        border: 1px solid ${stripBorder};
        max-height: 80px;
        transition: transform 120ms ease, box-shadow 120ms ease, filter 120ms ease, background-color 120ms ease;
        box-shadow: 0 4px 12px rgba(0,0,0,0.06);
        overflow: hidden;
      }

      .partners-strip__item img {
        display: block;
        max-height: 100px;
        max-width: 320px;
        object-fit: contain;
        filter: grayscale(100%);
      }

      .partners-strip__item:hover {
        transform: translateY(-1px);
        box-shadow: 0 8px 20px rgba(0,0,0,0.15);
        background: #ffffff;
      }

      .partners-strip__item:hover img {
        filter: grayscale(0%);
        opacity: 1;
      }

      /* Responsive tweaks */
      @media (max-width: 768px) {
        .partners-section {
          padding: 0 16px;
        }

        .partners-strip {
          border-radius: 16px;
          padding: 14px 16px 16px;
          gap: 12px;
        }
        .partners-strip__logos {
          justify-content: center;
        }
      }

      @media (max-width: 480px) {
        .partners-strip__item {
          padding: 4px 8px;
        }
        .partners-strip__item img {
          max-height: 20px;
          max-width: 90px;
        }
      }
    `;

    return (
        <>
            <style>{css}</style>
            <section aria-label="Partners and sponsors" className="partners-section">
                {/* Title + subtitle OUTSIDE the box */}
                <div className="partners-section__heading">
                    <h3 className="partners-section__title">{t('home.partners.title')}</h3>
                    <p className="partners-section__subtitle">
                        {t('home.partners.subtitle')}
                    </p>
                </div>

                <Divider style={{ marginBottom: 20 }} color={primaryColor} />

                {/* Logos box */}
                <div className="partners-strip">
                    <div className="partners-strip__logos">
                        {partners.map((p) => {
                            const key = p.id || p.slug || p.name;
                            const logo = p.logoUrl || p.logo || "";
                            const content = (
                                <div className="partners-strip__item" title={p.name}>
                                    {logo ? (
                                        <img src={logo} alt={p.name} />
                                    ) : (
                                        <span
                                            style={{
                                                fontSize: 11,
                                                fontWeight: 600,
                                                color: headingTitleColor,
                                                padding: "0 6px",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {p.name}
                                        </span>
                                    )}
                                </div>
                            );

                            if (p.href) {
                                return (
                                    <a
                                        key={key}
                                        href={p.href}
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label={p.name}
                                    >
                                        {content}
                                    </a>
                                );
                            }

                            return (
                                <div key={key} aria-label={p.name}>
                                    {content}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </>
    );
}
